/*
AI-GENERATED
365 Copilot
LLM: GPT-5.2
Tarkistanut Oskari Rintamäki
Date: 2026-05-08
*/

import React from 'react';
import styled from 'styled-components';

const StyledFeatureAction = styled.span`
  color: #0066cc;
  cursor: pointer;

  :hover {
    text-decoration: underline;
  }
`;

// -----------------------------------------------------------------------------
// Public renderer
// -----------------------------------------------------------------------------

/**
 * Renders:
 *  - Markdown links: [label](target)
 *      - if target is URL => <a href=...>
 *      - else if onActionLinkClick provided => StyledFeatureAction (clickable)
 *      - else => plain label
 *  - Plain URLs anywhere in text: https://... or www....
 *  - Preserves line breaks (\n)
 */
export const renderLinksInText = (text, onActionLinkClick) => {
  if (text == null) return null;

  const str = String(text);

  // Markdown links: [text](target)
  const markdownLinkRegex = /\[([^\]]+)\]\(([^)\s]+)\)/g;

  // Plain URLs: http(s)://... or www....
  const urlRegex = /(https?:\/\/[^\s<>"']+|www\.[^\s<>"']+)/gi;

  const nodes = [];
  let lastIndex = 0;
  let keyIndex = 0;

  // ---------- 1) Handle Markdown links first ----------
  let mdMatch;
  while ((mdMatch = markdownLinkRegex.exec(str)) !== null) {
    const matchStart = mdMatch.index;
    const matchText = mdMatch[0];
    const label = mdMatch[1];
    const target = mdMatch[2];

    // Push text before markdown link (and parse plain URLs within it)
    if (matchStart > lastIndex) {
      const before = str.slice(lastIndex, matchStart);
      pushTextWithLineBreaksAndUrls(nodes, before, urlRegex, `mdt-${keyIndex++}`);
    }

    // Render the markdown link itself
    if (isUrl(target)) {
      const href = safeEncodeUri(normalizeUrl(target));
      nodes.push(
        <a
          key={`md-url-${keyIndex++}`}
          href={href}
          target="_blank"
          rel="noopener noreferrer"
        >
          {label}
        </a>
      );
    } else if (onActionLinkClick) {
      const actionId = `feature-action-${keyIndex++}`; // stable id, no double increment
      nodes.push(
        <StyledFeatureAction
          key={`md-action-${actionId}`}
          id={actionId}
          className="gfi-feature-action-link"
          role="button"
          tabIndex={0}
          onClick={() => onActionLinkClick(target)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              onActionLinkClick(target);
            }
          }}
        >
          {label}
        </StyledFeatureAction>
      );
    } else {
      // No handler => render label as plain text
      nodes.push(<span key={`md-txt-${keyIndex++}`}>{label}</span>);
    }

    lastIndex = matchStart + matchText.length;
  }

  // ---------- 2) Remaining text after last markdown link ----------
  const remaining = str.slice(lastIndex);
  if (remaining) {
    pushTextWithLineBreaksAndUrls(nodes, remaining, urlRegex, `rest-${keyIndex++}`);
  }

  // ---------- 3) Fallback: no links found ----------
  if (nodes.length === 0) {
    return formatTextWithBreaks(str);
  }

  return nodes;
};

// -----------------------------------------------------------------------------
// Helpers
// -----------------------------------------------------------------------------

function isUrl(target) {
  return /^https?:\/\//i.test(target) || /^www\./i.test(target);
}

function normalizeUrl(url) {
  return /^https?:\/\//i.test(url) ? url : `http://${url}`;
}

function safeEncodeUri(url) {
  try {
    return encodeURI(url);
  } catch {
    return url;
  }
}

/**
 * Pushes text preserving \n line breaks.
 * Additionally, for each line segment, finds plain URLs and converts them to <a>.
 */
function pushTextWithLineBreaksAndUrls(targetArray, text, urlRegex, keyPrefix) {
  if (!text) return;

  // Split by newline to keep <br/>
  const lines = String(text).split('\n');

  lines.forEach((line, lineIdx) => {
    if (line) {
      // For each line, split into [text, url, text, url, ...]
      pushTextWithPlainUrls(targetArray, line, urlRegex, `${keyPrefix}-l-${lineIdx}`);
    }

    // Add <br/> between lines
    if (lineIdx !== lines.length - 1) {
      targetArray.push(<br key={`${keyPrefix}-br-${lineIdx}`} />);
    }
  });
}

/**
 * Parses plain URLs inside a single line and appends text/link nodes to targetArray.
 * Trims trailing punctuation from URL like ".)" etc (same idea as your old implementation).
 */
function pushTextWithPlainUrls(targetArray, line, urlRegex, keyPrefix) {
  let last = 0;
  let match;
  let idx = 0;

  // Reset regex state for safety when reusing global regex
  urlRegex.lastIndex = 0;

  while ((match = urlRegex.exec(line)) !== null) {
    const matchStart = match.index;
    const matchText = match[0];

    if (matchStart > last) {
      targetArray.push(
        <span key={`${keyPrefix}-t-${idx++}`}>
          {line.slice(last, matchStart)}
        </span>
      );
    }

    // Trim trailing punctuation (.,;:!?)]})
    let urlCoreEnd = matchText.length;
    const trailingPunct = '.,;:!?)]}';
    while (urlCoreEnd > 0 && trailingPunct.includes(matchText.charAt(urlCoreEnd - 1))) {
      urlCoreEnd--;
    }

    const core = matchText.slice(0, urlCoreEnd);
    const trail = matchText.slice(urlCoreEnd);

    const href = safeEncodeUri(normalizeUrl(core));
    const decoded = safeDecodeUri(core);

    targetArray.push(
      <a
        key={`${keyPrefix}-a-${idx++}`}
        href={href}
        target="_blank"
        rel="noopener noreferrer"
      >
        {decoded}
      </a>
    );

    if (trail) {
      targetArray.push(
        <span key={`${keyPrefix}-trail-${idx++}`}>{trail}</span>
      );
    }

    last = matchStart + matchText.length;
  }

  if (last < line.length) {
    targetArray.push(
      <span key={`${keyPrefix}-t-last-${idx++}`}>
        {line.slice(last)}
      </span>
    );
  }
}

function safeDecodeUri(url) {
  try {
    return decodeURI(url);
  } catch {
    return url;
  }
}

/**
 * Fallback renderer that preserves line breaks for whole text.
 */
function formatTextWithBreaks(text) {
  const parts = String(text).split('\n');
  return parts.map((p, i) => (
    <React.Fragment key={`fb-${i}`}>
      {p}
      {i !== parts.length - 1 && <br />}
    </React.Fragment>
  ));
}